import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, Users, Radio, Plane, Shield } from "lucide-react";
import { motion } from "framer-motion";

const features = [
  {
    title: "Incident Workflow Integration",
    description: "Helais integrates with existing incident management and dispatch systems. No requirement to replace working processes.",
    icon: Radio,
  },
  {
    title: "Multi-Asset Coordination",
    description: "Coordinate multiple aerial and ground assets from a single mission interface. Hand-off between operators and assets as situations evolve.",
    icon: Plane,
  },
  {
    title: "Third-Party UAV Support",
    description: "Helais supports qualified third-party UAVs and sensors. Use the equipment that meets your operational and procurement requirements.",
    icon: Shield,
  },
  {
    title: "Human Oversight",
    description: "Operators maintain authority over mission decisions. Helais supports—but does not replace—human judgement.",
    icon: Users,
  },
];

export default function PublicSafetyPage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/solutions" className="text-caption hover:text-foreground transition-colors mb-4 inline-block">
              Solutions
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <h1 className="text-display text-foreground mb-6">
              Public Safety & Emergency Response
            </h1>
            <p className="text-subtitle max-w-2xl mb-4">
              Mission orchestration through Helais
            </p>
            <p className="text-body max-w-2xl">
              Emergency response requires coordination, not just individual drones. Helais provides mission orchestration for aerial support workflows—integrating with existing systems and maintaining human oversight.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Positioning */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h2 className="text-headline text-foreground mb-6">
              Mission Orchestration, Not Just Drones
            </h2>
            <p className="text-body mb-4">
              Public safety agencies need more than individual UAV platforms. They need systems that integrate aerial assets into existing incident workflows, coordinate multiple units, and maintain clear lines of authority.
            </p>
            <p className="text-body">
              Helais is a mission orchestration platform. It can work with RLK airframe configurations or qualified third-party UAVs, depending on your operational and procurement requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Capabilities</p>
            <h2 className="text-headline text-foreground">Helais for Public Safety</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="card-premium p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center mb-4">
                  <feature.icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-label text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dependencies */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-headline text-foreground mb-6 text-center">
              Deployment Dependencies
            </h2>
            <div className="card-pillar p-8">
              <p className="text-body mb-6">
                Helais deployment for public safety requires clear understanding of regulatory requirements and operational boundaries:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Aviation authority approvals and operational limitations</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">BVLOS requirements where applicable</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Integration with existing CAD/dispatch systems</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Operator training and certification</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Data handling and evidence requirements</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-6 italic">
                Regulatory and BVLOS requirements are deployment dependencies, not solved assumptions. We work with you to understand and address these requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Discuss Mission Integration
            </h2>
            <p className="text-body mb-8">
              Tell us about your operational requirements, existing systems, and deployment constraints. We will scope a pilot that demonstrates integration value.
            </p>
            <Link to="/contact?type=public-safety" className="btn-primary">
              Scope a Pilot
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
